<template>
  <div
    :class="getClass"
    class="color-picker" >
    <div class="first-column">
      <Saturation
        ref="saturation"
        :color="rgbString"
        :hsv="hsv"
        :size="hueHeight"
        @selectSaturation="selectSaturation"
      />
      <Hue
        ref="hue"
        :hsv="hsv"
        :width="hueWidth"
        :height="hueHeight"
        @selectHue="selectHue"
      />
      <Alpha
        ref="alpha"
        :color="rgbString"
        :rgba="rgba"
        :width="hueWidth"
        :height="hueHeight"
        @selectAlpha="selectAlpha"
      />
    </div>
    <div v-if="activeObject.isGradient">
      <GradientHue :active-object="activeObject" @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
      @updateSelectedPointPosition="updateSelectedPointPosition"/>

      <div class="effects-subpanel">
        <div class="fs-12 box-name">Angle</div>
        <div class="effect-container">
          <input
              id="distance-range"
              min="0"
              step="1"
              max="90"
              :value="activeObject.gradient.angle"
              @input="updateGradientAngle($event.target.value)"
              type="range"
              class="slider rounded rounded-12 btn-outline-0"
          />
          <div class="box d-flex justify-content-center align-items-center">
            <span class="box-value">{{ Math.round(activeObject.gradient.angle) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="third-column">
      <div class="top-row tool-row">
        <div class="preview-tool">
          <div class="preview-title">Selected Color</div>
          <div class="preview-control">
            <Preview
              :color="hexString"
              :width="36"
              :height="36"
            />
            <Box
              :color="modelHex"
              name="RGBA"
              @inputColor="inputRgba"
            />
          </div>
        </div>
        <div
          v-if="isValidBrowser"
          class="other-tool tool-box">
          <div class="preview-title">Other</div>
          <div
            v-if="eyedropper"
            class="preview-control">
            <label
              class="bm-small-btns custom-icon d-flex align-items-center justify-content-center tooltip-button"
              @click="showEyeDropper"
            >
              <icon name="eyedropper"/>

            </label>
          </div>
          <div
            v-else
            class="preview-control">
            <label
              class="bm-small-btns custom-icon d-flex align-items-center justify-content-center tooltip-button"
            >
              <icon name="eyedropper"/>
              <input
                :value="hexString"
                type="color"
                style="width: 0px; height: 0px; visibility: hidden"
                @change="updateColorFromNativePicker"
                @input="updateColorFromNativePicker">
            </label>
          </div>
        </div>
      </div>
      <div class="recently-used-color tool-row">
        <div class="preview-title">Recently Used Colors</div>
        <History @selectColor="selectColor"/>
      </div>
      <div class="your-color tool-row">
        <div class="preview-title">Product Colors</div>
        <Colors
          :is-processing="productColorProcessing"
          :color="rgbaString"
          :colors-default="$store.getters.getProductColor"
          @selectColor="selectColor"
        />
      </div>
      <div class="your-color tool-row">
        <div class="preview-title">Your Colors</div>
        <Colors
          :color="rgbaString"
          :colors-default="$store.getters.getAllYourColors"
          @selectColor="selectColor"
        />
      </div>

    </div>


  </div>
</template>

<script>
import EventBus from '@/EventBus';
import _ from 'lodash';
import Bowser from 'bowser';
import mixin from './mixin';
import Saturation from './Saturation.vue';
import Hue from './Hue.vue';
import Alpha from './Alpha.vue';
import Preview from './Preview.vue';
import Sucker from './Sucker.vue';
import Box from './Box.vue';
import Colors from './Colors.vue';
import History from './History.vue';
import GradientHue from "./GradientHue";

export default {
  components: {
    Saturation,
    Hue,
    Alpha,
    Preview,
    Sucker,
    Box,
    Colors,
    History,
    GradientHue
  },
  mixins: [mixin],
  props:  {
    color: {
      type:    String,
      default: '#000000',
    },
    theme: {
      type:    String,
      default: 'dark',
    },
    suckerHide: {
      type:    Boolean,
      default: true,
    },
    suckerCanvas: {
      type:    null, // HTMLCanvasElement
      default: null,
    },
    suckerArea: {
      type:    Array,
      default: () => [],
    },
    displayStyle: {
      type:    String,
      default: 'float',
    },
    colorsDefault: {
      type:    Array,
      default: () => [
        '#000000', '#FFFFFF', '#FF1900', '#F47365', '#FFB243', '#FFE623', '#6EFF2A', '#1BC7B1',
        '#00BEFF', '#2E81FF', '#5D61FF', '#FF89CF', '#FC3CAD', '#BF3DCE', '#8E00A7', 'rgba(0,0,0,0)',
      ],
    },
    colorsHistoryKey: {
      type:    String,
      default: 'bm-colorpicker-history',
    },
    activeObject:{
      type: Object,
      default: {}
    }
  },
  data() {
    return {
      hueWidth:               26,
      hueHeight:              224,
      previewHeight:          30,
      modelRgba:              '',
      modelHex:               '',
      r:                      0,
      g:                      0,
      b:                      0,
      a:                      1,
      h:                      0,
      s:                      0,
      v:                      0,
      productColorProcessing: false,
      eyedropper:             null,
      isValidBrowser:         false,
    };
  },
  computed: {
    getClass() {
      return this.displayStyle === 'inline' ? 'inline-picker' : '';
    },
    previewWidth() {
      return this.totalWidth - (this.suckerHide ? 0 : this.previewHeight);
    },
    rgba() {
      return {
        r: this.r,
        g: this.g,
        b: this.b,
        a: this.a,
      };
    },
    hsv() {
      return {
        h: this.h,
        s: this.s,
        v: this.v,
      };
    },
    rgbString() {
      return `rgb(${this.r}, ${this.g}, ${this.b})`;
    },
    rgbaStringShort() {
      return `${this.r}, ${this.g}, ${this.b}, ${this.a}`;
    },
    rgbaString() {
      return `rgba(${this.rgbaStringShort})`;
    },
    hexString() {
      return this.rgb2hex(this.rgba, true);
    },
  },
  mounted() {
    EventBus.$on('StartProductColorProcessing', this.startProductColorProcessing);
    EventBus.$on('StopProductColorProcessing', this.stopProductColorProcessing);
    const browser       = Bowser.getParser(window.navigator.userAgent);
    this.isValidBrowser = browser.satisfies({
      // or in general
      chrome: '>=80',
      opera:  '>=22',
      edge:   '>=90',
    });
    try {
      this.eyedropper = new EyeDropper();
    } catch (ex) {
      console.log('Eyedropper cannot be init', ex);
    }
    console.log(`The current browser name is "${browser.getBrowserName()}" ver:${browser.getBrowserVersion()}`, `Your Eyedropper visibility will be ${this.isValidBrowser}`);
  },
  beforeDestroy() {
    EventBus.$off('StartProductColorProcessing');
    EventBus.$off('StopProductColorProcessing');
  },
  created() {
    Object.assign(this, this.setColorValue(this.color));
    this.setText();

    this.$watch('rgba', () => {
      this.$emit('changeColor', {
        rgba: this.rgba,
        hsv:  this.hsv,
        hex:  this.modelHex,
      });
    });
  },

  methods: {
    updateGradientAngle(angle) {
      this.$emit('updateSelectedGradientAngle', angle);
    },
    updateSelectedPointPosition(position) {
      this.$emit('updateSelectedPointPosition', position);
    },
    updateSelectedPointOfGradient(index) {
      this.$emit('updateSelectedPointOfGradient', index);
    },
    startProductColorProcessing() {
      this.productColorProcessing = true;
    },
    stopProductColorProcessing() {
      this.productColorProcessing = false;
    },
    selectSaturation(color) {
      const {
        r, g, b, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, h, s, v,
      });
      this.setText();
    },
    selectHue(color) {
      const {
        r, g, b, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, h, s, v,
      });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
      });
    },
    showEyeDropper() {
      const that = this;
      this.eyedropper.open().then((colorSelectionResult) => {
        that.selectColor(colorSelectionResult.sRGBHex);
      })
        .catch((error) => {
          console.log(error);
        });
    },
    updateColorFromNativePicker(colorSelectionResult) {
      const that = this;
      that.selectColor(colorSelectionResult.target.value);
    },
    selectAlpha(a) {
      this.a = a;
      _.debounce(this.setText, 200);
    },
    inputHex(color) {
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.modelHex  = color;
      this.modelRgba = this.rgbaStringShort;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    inputRgba(color) {
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.modelHex  = this.hexString;
      this.modelRgba = color;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    setText() {
      this.modelHex  = this.hexString;
      this.modelRgba = this.rgbaStringShort;
    },
    selectColor(color) {
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
  },
};
</script>

<style lang="scss">

.color-picker {
  width: 608px;
  padding: 20px;
  background: white;
  border-radius: 12px !important;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  z-index: 1;
  display: flex;
  position: fixed;
  &.inline-picker{
    padding: 5px 20px 0px 20px;
  }
  canvas{
    border-radius: 5px !important;
    border: solid thin #ededed;
  }
  .tool-row{
    margin-bottom: 14px;
    &:last-child{
      margin-bottom: 0px;
    }
  }
  .color-alert{
    font-size: 14px;
    color: red;
    margin-top: 8px;
    margin-left: 2px;
  }

  .first-column{
    display: flex;
    .saturation {
      position: relative;
      cursor: pointer;

      .slide {
        position: absolute;
        left: 100px;
        top: 0;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1px solid #fff;
        box-shadow: 0 0 1px 1px rgba(0, 0, 0, 0.3);
        pointer-events: none;
      }
    }
    .hue {
      position: relative;
      margin-left: 20px;
      cursor: pointer;
      width: 26px;
      .slide {
        position: absolute;
        left: -3px;
        top: 100px;
        right: -5px;
        height: 6px;
        border: solid 1px #202020;
        border-radius: 5px !important;

        pointer-events: none;
      }
    }
  }

  .color-show {
    margin-top: 8px;
    display: flex;
    .sucker {
      background: #eceef0;
    }
  }

  .third-column {
    margin-left: 20px;
    .color-type {
      display: flex;
      margin-left: 8px;
      .value {
        height: 36px;
        width: 84px;
        padding: 6px;
        color: #3F3F3F;
        border: solid thin #EDEDED;
        box-shadow: inset 0px 0px 6px 0px #EDEDED;
        border-radius: 5px;
        box-sizing: border-box;
        font-size: 12px;
        font-family: 'Open Sans';
      }
    }
    .other-tool{
      margin-left: 20px;
    }
    .preview-control{
      display: flex;
    }
    .preview-title {
      font-family: 'Open Sans';
      font-size: 12px;
      line-height: 20px;
      color: #5F5F5F;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .top-row{
      display: flex;

    }
    .colors {
      padding: 0;
      margin: 0;
      .item {
        position: relative;
        width: 20px;
        height: 20px;
        margin: 0px 8px 6px 0px;
        border-radius: 3px;
        box-sizing: border-box;
        vertical-align: top;
        display: inline-block;
        transition: all 0.1s;
        cursor: pointer;
        border: solid thin #ccc;
        &:hover {
          transform: scale(1.4);
        }
        .alpha {
          height: 100%;
          border-radius: 4px; // 大一像素，否则四个角会看到白点
        }
        .color {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          border-radius: 3px;
        }
      }
    }
  }

  &.inline-picker{
    position: relative;
    flex-direction: column;
    width: 100%;
    box-shadow: none;
    .third-column{
      margin-left: 0px;
      padding: 10px 0px;
    }
  }

  .slider {
    &:focus {
      border: none;
    }

    -webkit-appearance: none;
    height: 4px;
    background: linear-gradient(90deg, rgb(214, 214, 214) 60%, rgb(214, 214, 214) 60%);
    opacity: 0.7;
    -webkit-transition: 0.2s;
    transition-opacity: 0.2s;
  }

  .effects-subpanel {
    .effect-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      input {
        width: 220px;
      }

      .box-effects {
        border-radius: 8px;
        font-size: large;
        width: 50px;
        height: 35px;
        background: white;
      }
    }
  }

  .box-name {
    margin-top: 5px;
    height: 20px;
    font-family: Open Sans;
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 20px;
    text-transform: uppercase;

    color: #5F5F5F;

  }

  .box {
    background: #FFFFFF;
    border: 1px solid #EDEDED;
    box-sizing: border-box;
    box-shadow: inset 0px 0px 8px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    width: 60px;
    height: 36px;
  }

  .box-value {
    font-family: Open Sans;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 24px;
    text-align: center;
    color: #3F3F3F;
  }
}
</style>
